import { Box, Flex, Icon, Image, Text, useBreakpointValue } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { permissions, routes, sideBarPages } from "../../../config";
import { useAppConfig, useAuth } from "../../../context";
import { isAllowedToView, isModuleAvailable } from "utils/AuthUtils";
import * as React from "react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import antaiaLogo from "../../../assets/img/antaia-logo.png";
import useOrgLogo from "../useOrgLogo";

// TODO refactor this to reduce copy-paste and complexity of code for menu, sub-menu

/**
 * Left sidebar
 */
function Sidebar() {
  const { storedSidebarState, sidebarState } = useAppConfig();
  // const { userPermissions } = useAuth();
  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];
  const [openDropdown, setOpenDropdown] = useState(null);

  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  useEffect(() => {
    if (!sidebarOpened) {
      setOpenDropdown(null);
    }
  }, [sidebarOpened]);

  const sidebarContentW = useBreakpointValue({
    base: sidebarState ? "100vw" : 0,
    md: sidebarState ? "64" : "14",
    lg: storedSidebarState ? "64" : "14",
  });

  const sidebarContentZIndex = useBreakpointValue({
    base: 10,
    md: 1,
  });

  // Live measurement of how far right the nav labels' actual glyphs reach, so left-aligned logos
  // can be capped at the true "farthest right letter" rather than a guessed label-box edge (labels
  // are laid out in a fixed-width box — `width: "70%"` on the Text below — so the box's own rect is
  // NOT the text's real extent whenever a label doesn't fill it or wraps to a shorter second line).
  // Only top-level labels are measured (`data-nav-label`, set only when !hideIcon) — collapsed
  // sub-menu items aren't part of what's visibly on screen at once. Re-measured on expand/collapse
  // and once fonts finish loading (initial paint can use a fallback web-safe metric before swap).
  const sidebarRef = useRef(null);
  const navListRef = useRef(null);
  const [labelRightEdge, setLabelRightEdge] = useState(null);

  useLayoutEffect(() => {
    if (!sidebarOpened) {
      setLabelRightEdge(null);
      return;
    }
    const measure = () => {
      const container = sidebarRef.current;
      const list = navListRef.current;
      if (!container || !list) return;
      const labels = list.querySelectorAll('[data-nav-label="true"]');
      if (labels.length === 0) return;
      const containerLeft = container.getBoundingClientRect().left;
      let maxRight = -Infinity;
      labels.forEach((el) => {
        const range = document.createRange();
        range.selectNodeContents(el);
        Array.from(range.getClientRects()).forEach((rect) => {
          if (rect.width > 0) maxRight = Math.max(maxRight, rect.right);
        });
      });
      if (maxRight === -Infinity) return;
      setLabelRightEdge(Math.round(maxRight - containerLeft));
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (navListRef.current) ro.observe(navListRef.current);
    document.fonts?.ready?.then(measure);
    return () => ro.disconnect();
    // userPermissions intentionally excluded — a permission change resizes navListRef (items
    // added/removed change its height), which the ResizeObserver above already re-measures on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpened]);

  return (
    <Flex
      id="sidebar"
      ref={sidebarRef}
      flexDir="column"
      inlineSize={sidebarContentW}
      blockSize="full"
      overflow="hidden"
      zIndex={sidebarContentZIndex}
      transitionTimingFunction="ease"
      transitionDuration="200ms"
      backgroundColor="darkTeal"
    >
      <SidebarHeader labelRightEdge={labelRightEdge} />
      <Box
        ref={navListRef}
        flexGrow={1}
        overflowY="auto"
        overflowX="hidden"
        sx={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "transparent",
            borderRadius: "full",
            transition: "background 0.2s",
          },
          "&:hover::-webkit-scrollbar-thumb": { background: "whiteAlpha.300" },
          "&::-webkit-scrollbar-thumb:hover": { background: "whiteAlpha.500" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "rgba(255,255,255,0.3) transparent" },
        }}
      >
        {sideBarPages
          // Check if the page is available for the current organization, only if it's associated to module
          .filter((menuItem) => isModuleAvailable(menuItem.module))
          .filter((menuItem) =>
            isAllowedToView(userPermissions, menuItem.linkProps.requiredPermissions, menuItem.linkProps.requiredModules)
          )
          .map((menuItem) => (
            <Box key={menuItem.name}>
                {menuItem.subMenus && menuItem.subMenus.length > 0 ? (
                  <SidebarItemWithSubMenus {...menuItem} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
                ) : (
                  <SidebarItem {...menuItem} />
                )}
            </Box>
          ))}
      </Box>
      <SidebarFooter userPermissions={userPermissions} />
    </Flex>
  );
}

function CollapseMenu({ mainItem, subItems, isActive, isOpen, onToggle, sideBarOpened }) {
  return (
    <Box cursor="pointer">
      <Flex
        onClick={onToggle}
        alignItems="center"
        fontWeight="semibold"
        _hover={{
          opacity: 1,
          bg: "teal",
        }}
      >
        {mainItem}
        <Box width="15%">
          {isOpen ? <FaChevronUp opacity={isActive ? 1 : 0.5} color="white" /> : <FaChevronDown color="white" />}
        </Box>
      </Flex>
      {isOpen && (
        <Box bg="whiteShade.700" margin={sideBarOpened ? "10px" : "2px"} padding="5px" borderRadius="8px">
          <Box borderLeft="1px solid" borderColor="whiteShade.600" margin={sideBarOpened ? "15px" : "0px"}>
            {subItems}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function SidebarItemWithSubMenus({
  name,
  mainRoute,
  linkProps: { icon, to },
  subMenus,
  openDropdown,
  setOpenDropdown,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const { storedSidebarState, sidebarState, toggleSidebar } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  const isParentItemActive =
    location.pathname.includes(mainRoute) && subMenus.some((sub) => sub.pathProps.path === location.pathname);

  const isOpen = openDropdown === name;

  const handleToggle = () => {
    if (!sidebarOpened) {
      toggleSidebar();
      setOpenDropdown(name);
    } else {
      setOpenDropdown(isOpen ? null : name);
    }
  };

  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];

  const mainItem = (
    <Flex
      inlineSize={56}
      alignItems="center"
      fontSize="body.s"
      paddingInlineStart="4"
      paddingBlock="3"
      position="relative"
      fontWeight="semibold"
      width="100%"
      _hover={{
        opacity: 1,
        bg: "teal",
      }}
      title={sidebarOpened ? "" : t(`pages.${name}`)}
      color={isParentItemActive ? "red" : "whiteShade.300"}
    >
      <Icon fontSize="lg" marginInlineEnd="6" as={icon} color={isParentItemActive ? "lime" : "whiteShade.300"} />
      {sidebarOpened && (
        <Text
          data-nav-label="true"
          fontSize="body.s"
          color={isParentItemActive ? "lime" : "whiteShade.300"}
          width={"70%"}
        >
          {t(`pages.${name}`)}
        </Text>
      )}
    </Flex>
  );

  const visibleSubMenus = subMenus.filter((subMenu) =>
    !subMenu.hidden && isAllowedToView(userPermissions, subMenu.linkProps.requiredPermissions, subMenu.linkProps.requiredModules)
  );

  // Group headers are currently only rendered for the Settings menu, where
  // group names are translation keys (e.g. "settingsGroupOrganisation").
  // Other sections (e.g. Supplier Portal) have legacy pre-translated group
  // names we deliberately leave unlabelled.
  const isSettingsMenu = name === "settings";
  const distinctGroups = new Set(
    visibleSubMenus.map((sm) => sm.linkProps?.group?.name).filter(Boolean)
  );
  const showGroupHeaders = isSettingsMenu && distinctGroups.size >= 2;

  const subItems = visibleSubMenus.map((subMenu, idx) => {
    const groupName = subMenu.linkProps?.group?.name;
    const prevGroupName = idx > 0 ? visibleSubMenus[idx - 1].linkProps?.group?.name : null;
    const isFirstInGroup = showGroupHeaders && groupName && groupName !== prevGroupName;
    return (
      <Box key={subMenu.name}>
        {isFirstInGroup && (
          <Text
            px={4}
            pt={idx === 0 ? 1 : 3}
            pb={1}
            fontSize="10px"
            fontWeight="700"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="whiteShade.500"
          >
            {t(`pages.${groupName}`)}
          </Text>
        )}
        <SidebarItem {...subMenu} hideIcon={true} />
      </Box>
    );
  });
  return (
    <Fragment>
      {to ? (
        <NavLink to={to}>{mainItem}</NavLink>
      ) : (
        <CollapseMenu
          mainItem={mainItem}
          subItems={subItems}
          isActive={isParentItemActive}
          isOpen={isOpen}
          onToggle={handleToggle}
          sideBarOpened={sidebarOpened}
        />
      )}
    </Fragment>
  );
}

function SidebarItem({
  name,
  linkProps: { icon, to, marginStart = 0, textFontSize = "body.s", iconFontSize = "lg" },
  hideIcon = false,
}) {
  const { t } = useTranslation();
  const { storedSidebarState, sidebarState } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  if (!to) {
    console.error("SidebarItem: to is not set for ", name);
    return (
      <Box>
        <Text>{name}</Text>
      </Box>
    );
  }

  return (
    <NavLink to={to}>
      {({ isActive }) => (
        <Flex
          inlineSize={56}
          alignItems="center"
          fontSize="body.s"
          paddingInlineStart="4"
          paddingBlock="2"
          position="relative"
          fontWeight="semibold"
          width="100%"
          _hover={{
            opacity: 1,
          }}
          title={sidebarOpened ? "" : t(`pages.${name}`)}
        >
          {(!sidebarOpened || !hideIcon) && (
            <Icon
              fontSize={iconFontSize}
              marginStart={marginStart}
              marginInlineEnd="6"
              as={icon}
              color={isActive ? "lime" : "whiteShade.300"}
            />
          )}
          {sidebarOpened && (
            <Text
              data-nav-label={hideIcon ? undefined : "true"}
              fontSize={textFontSize}
              marginStart={marginStart}
              color={isActive ? "lime" : "whiteShade.300"}
              width={"70%"}
            >
              {t(`pages.${name}`)}
            </Text>
          )}
        </Flex>
      )}
    </NavLink>
  );
}

function SidebarHeader({ labelRightEdge }) {

  const navigate = useNavigate();
  // const { userPermissions } = useAuth();
  const authContext = useAuth?.();
  const userPermissions = authContext ? authContext.userPermissions : [];

  // Per-org settings (Organization Management → Organization Logo Location/Alignment), persisted in
  // organization.logo_style. Unset reads as "left" (sidebar) and "center", matching the app's behaviour
  // before these settings existed. useOrgLogo also resolves the image, including the bundled-subdomain
  // fallback for orgs with no logoUrl — see that hook for why the fallback used to be dead code.
  const { logoSrc, alignment: resolvedLogoAlign, showCustomerLogoInSidebar } = useOrgLogo();

  const { storedSidebarState } = useAppConfig();
  const isCollapsed = !storedSidebarState;
  const sidebarWidthPx = isCollapsed ? 56 : 256;

  // Left mode aligns to the nav rows' own icon inset (16px, paddingInlineStart="4" on every SidebarItem)
  // and caps at the farthest-right pixel any nav label's actual glyphs reach — measured live by the
  // parent Sidebar (`labelRightEdge`, via data-nav-label + Range.getClientRects(), not the label's
  // declared 70%-width CSS box, which is usually wider than the text actually rendered inside it) and
  // passed down here. `measuredCapPx` converts that absolute-from-sidebar-edge measurement into a width
  // for this box, which shares the same 16px left inset. Falls back to a rough icon-inset-relative guess
  // only before the first measurement lands or when nothing was measurable (no labels — e.g. first paint
  // or, in principle, an empty nav). That 16px inset doesn't change when collapsed (only the label text
  // unmounts), so left-align still pins there when collapsed too — just capped to whatever room the 56px
  // rail actually has, instead of falling back to centered (which used to make left vs. center
  // indistinguishable whenever the sidebar happened to be collapsed, regardless of which was picked).
  const useLeftLayout = resolvedLogoAlign === "left";
  const boxAlignProps = useLeftLayout
    ? { justifyContent: "flex-start", pl: "16px" }
    : { justifyContent: "center", px: 3 };
  const fallbackCapPx = Math.min(199, Math.max(24, sidebarWidthPx - 24));
  const measuredCapPx = labelRightEdge != null ? Math.max(24, labelRightEdge - 16) : null;
  const logoMaxW = useLeftLayout
    ? `${!isCollapsed && measuredCapPx != null ? measuredCapPx : fallbackCapPx}px`
    : `${Math.round(sidebarWidthPx * 0.75)}px`;

  // Location "top" moves the logo image itself to the Navbar, but the gap it sits in stays — this
  // box is what gives the first nav row (Dashboard) its breathing room from the sidebar's top edge,
  // and dropping the whole box whenever there's no logo to show here left that row flush against the
  // very top with no spacer at all.
  // Left-nav client logo only shows while the sidebar is maximised — collapsed (56px, icons-only) has
  // no room to do it justice, and hiding it there keeps the collapsed rail purely icon-based like the
  // rest of the nav rows. That collapse rule is the one bit of this the hook can't decide, since the
  // footer's notion of "open" is a different value.
  const showLogoImage = showCustomerLogoInSidebar && !isCollapsed;

  return (
    <Box flexShrink={0} flexGrow={0} pt={7} pb={3} display="flex" {...boxAlignProps}>
      {showLogoImage && (
        <Image
          w={logoMaxW}
          maxH="120px"
          objectFit="contain"
          src={logoSrc}
          cursor="pointer"
          onClick={() => navigate(getDashboardRoute(userPermissions))}
        />
      )}
    </Box>
  );
}

function SidebarFooter({ userPermissions }) {
  const { storedSidebarState, sidebarState } = useAppConfig();
  const sidebarOpened = useBreakpointValue({
    base: sidebarState,
    lg: storedSidebarState,
  });

  // "Hide Antaia Logo" (only offered once the customer logo is in the top navigation) moves the Antaia
  // mark OUT of the top bar and down here instead. Both halves of that move come from one expression in
  // utils/logoStyle.js — showAntaiaInTopBar is its exact complement — so the mark cannot end up in both
  // places or neither. Do not re-derive it from orgData here.
  const { showAntaiaBadgeInSidebarFooter } = useOrgLogo();

  if (!sidebarOpened || !showAntaiaBadgeInSidebarFooter) return null;

  return (
    <Flex flexDir="column" alignItems="center" justifyContent="center" gap={1} mb={4} w="100%">
      {/* No dedicated white/mono asset exists — brightness(0) invert(...) turns the (opaque, alpha-
          masked) two-tone antaia-logo.png into a solid off-white silhouette on the dark sidebar.
          invert(0.8) rather than invert(1) — pure white read as too stark/bright against the dark bg. */}
      <Image src={antaiaLogo} maxH="32px" objectFit="contain" filter="brightness(0) invert(0.8)" />
      <Text fontSize="10px" color="whiteShade.400" whiteSpace="nowrap">
        Powered by Antaia
      </Text>
    </Flex>
  );
}

const getDashboardRoute = (userPermissions) => {
  const dashboardRouteObjects = [
    { permissions: permissions.dashboard, route: routes.dashboard },
    { permissions: permissions.supplierDashboard, route: routes.supplierDashboard },
    {
      permissions: permissions.dataCollectionOrganisationQuestionnaires,
      route: routes.dataCollectionOrganisationQuestionnaires,
    },
  ];

  return dashboardRouteObjects.find((obj) => isAllowedToView(userPermissions, obj.permissions))?.route ?? "";
};

export default Sidebar;
